import {
    AdfoxInfoForm,
    AdfoxPlacement,
    AdfoxTapTarget,
    CLICK_URL_MACRO,
    isBrowserTapAllowed,
    isPromoPlacement,
} from 'components/Admin/AdfoxInfo/adfoxInfoModel';

export type AdfoxInfoRequiredField = 'ctaLabel' | 'advertiserName' | 'advertiserInn' | 'erid';

export type AdfoxInfoValue = Record<string, unknown>;

export type AdfoxInfoBuildResult =
    | { kind: 'json'; value: AdfoxInfoValue }
    | { kind: 'empty' }
    | {
          kind: 'invalid';
          missingFields: AdfoxInfoRequiredField[];
          unclosedQuoteFields: AdfoxInfoRequiredField[];
          backslashFields: AdfoxInfoRequiredField[];
      };

const buildBannerInfo = (form: AdfoxInfoForm): AdfoxInfoBuildResult => {
    if (isBrowserTapAllowed(form.placement) && form.tapTarget === AdfoxTapTarget.Browser) {
        return { kind: 'empty' };
    }

    return { kind: 'json', value: { clickUrl: CLICK_URL_MACRO } };
};

const normalizeText = (value: string): string => value.trim().replace(/"([^"]*)"/g, '«$1»');

const buildPromoInfo = (form: AdfoxInfoForm): AdfoxInfoBuildResult => {
    const values: Record<AdfoxInfoRequiredField, string> = {
        ctaLabel: normalizeText(form.ctaLabel),
        advertiserName: normalizeText(form.advertiserName),
        advertiserInn: normalizeText(form.advertiserInn),
        erid: normalizeText(form.erid),
    };
    const fields: AdfoxInfoRequiredField[] = form.isAdvertising
        ? ['ctaLabel', 'advertiserName', 'advertiserInn', 'erid']
        : ['ctaLabel'];

    const missingFields = fields.filter((field) => !values[field]);
    const unclosedQuoteFields = fields.filter((field) => values[field].includes('"'));
    const backslashFields = fields.filter((field) => values[field].includes('\\'));
    if (missingFields.length > 0 || unclosedQuoteFields.length > 0 || backslashFields.length > 0) {
        return { kind: 'invalid', missingFields, unclosedQuoteFields, backslashFields };
    }

    const isFullScreen = form.placement === AdfoxPlacement.FullScreen;

    const value: AdfoxInfoValue = {
        ctaButton: {
            label: values.ctaLabel,
            clickUrl: CLICK_URL_MACRO,
            internal: !form.openCtaInExternalBrowser,
            ...(isFullScreen ? { style: form.ctaButtonStyle } : {}),
        },
        ...(isFullScreen ? { closeButton: { color: form.closeButtonColor } } : {}),
        ...(form.isAdvertising
            ? {
                  ordInfo: {
                      advertiser: { name: values.advertiserName, inn: values.advertiserInn },
                      erid: values.erid,
                  },
              }
            : {}),
    };

    return { kind: 'json', value };
};

export const buildAdfoxInfo = (form: AdfoxInfoForm): AdfoxInfoBuildResult =>
    isPromoPlacement(form.placement) ? buildPromoInfo(form) : buildBannerInfo(form);

export const formatAdfoxInfoForCabinet = (value: AdfoxInfoValue): string => JSON.stringify(value).replace(/"/g, '\\"');

export const formatAdfoxInfoPretty = (value: AdfoxInfoValue): string => JSON.stringify(value, null, 2);
